import React, { useState } from 'react';
import { forgetSavedChart, readSavedChart } from '../../services/savedChart';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import './Privacy.css';

function ForgetChart() {
  const [gone, setGone] = useState(false);
  const saved = !gone && readSavedChart();
  if (!saved) return null;
  return (
    <p>
      <button
        type="button"
        onClick={() => {
          forgetSavedChart();
          setGone(true);
        }}
      >
        Forget saved birth chart
      </button>
    </p>
  );
}

const Privacy = () => {
  return (
    <>
      <Header />
      <div className="privacy-container">
        <div className="privacy-content">
          <h1>Privacy Policy</h1>

          <p><em>Last updated: July 15, 2025</em></p>

          <p>Thank you for trusting <strong>NowTarot</strong> ("<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>") with your information. This Privacy Policy ("<strong>Policy</strong>") explains how we collect, use, disclose, and protect your personal information when you access or use our website, applications, and related services (collectively, the "<strong>Platform</strong>"). It also describes your rights and choices regarding your information.</p>

          <p>By using the Platform, you acknowledge that you have read and understood this Policy. If you do not agree with its terms, please do not use the Platform.</p>

          <hr />

          <h2>1. Scope</h2>
          <p>This Policy applies to information we collect from visitors, users, and account holders in connection with the Platform. It does <strong>not</strong> apply to third‑party websites, products, or services that may be linked to or integrated with our Platform.</p>

          <hr />

          <h2>2. Information We Collect</h2>

          <h3>2.1 Information You Provide Directly</h3>
          <p>When you interact with the Platform, we collect the following information:</p>
          <ul>
            <li><strong>Identification & Contact Details</strong>: Email address, full name, and phone number.</li>
            <li><strong>Account & Profile Details</strong>: Password (hashed), preferences, and any profile information you choose to add.</li>
            <li><strong>User Content</strong>: Questions you ask the AI and the AI‑generated answers (collectively "User Content").</li>
          </ul>

          <h3>2.2 Information We Collect Automatically</h3>
          <p>When you access the Platform, we automatically collect:</p>
          <ul>
            <li><strong>Technical Data</strong>: IP address, device identifiers, browser type, operating system, referring URLs, and country.</li>
            <li><strong>Usage Data</strong>: Timestamps, pages viewed, features used, interaction logs, and diagnostic reports.</li>
            <li><strong>Cookies & Similar Technologies</strong>: See Section 7 for details.</li>
          </ul>

          <h3>2.3 Information from Third‑Party Sources</h3>
          <p>We may receive information from:</p>
          <ul>
            <li><strong>Analytics Partners</strong> (e.g., Google Analytics) — aggregated statistics and engagement metrics.</li>
          </ul>

          <hr />

          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ol>
            <li><strong>Operate and maintain</strong> the Platform.</li>
            <li><strong>Provide, personalize, and improve</strong> our AI models and user experience.</li>
            <li><strong>Maintain account balances</strong> and service history.</li>
            <li><strong>Communicate</strong> with you about updates, security alerts, and support.</li>
            <li><strong>Conduct research and analytics</strong> to enhance features and develop new services.</li>
            <li><strong>Enforce</strong> our Terms of Service and other policies.</li>
            <li><strong>Prevent fraud, security breaches, and abuse</strong>.</li>
            <li><strong>Comply</strong> with legal obligations.</li>
          </ol>

          <blockquote>
            <p><strong>No Public Display of User Content:</strong> We <strong>do not</strong> publish or publicly display your questions or answers without your explicit consent.</p>
          </blockquote>

          <hr />

          <h2>4. Legal Bases for Processing (EEA/UK Users)</h2>
          <p>If you are located in the European Economic Area (EEA) or the United Kingdom (UK), our legal bases include:</p>
          <ul>
            <li><strong>Contractual Necessity</strong> — providing the services you request.</li>
            <li><strong>Legitimate Interests</strong> — improving and securing the Platform.</li>
            <li><strong>Consent</strong> — sending marketing communications or processing optional data (you may withdraw consent at any time).</li>
            <li><strong>Legal Obligation</strong> — compliance with applicable laws.</li>
          </ul>

          <hr />

          <h2>5. How We Share Information</h2>
          <p>We do <strong>not</strong> sell personal information. We share it only as described below:</p>
          <ol>
            <li><strong>Service Providers</strong>: Hosting, analytics, customer support.</li>
            <li><strong>Business Transfers</strong>: In connection with a merger, acquisition, or sale of assets.</li>
            <li><strong>Legal Requirements</strong>: To comply with subpoenas, court orders, or other legal processes, or to protect rights, property, or safety.</li>
            <li><strong>With Your Consent</strong>: When you expressly authorize us to share.</li>
          </ol>

          <hr />

          <h2>6. Data Retention</h2>
          <p>We retain personal information <strong>for as long as your account is active</strong> or as needed to:</p>
          <ul>
            <li>Provide the Platform.</li>
            <li>Fulfill the purposes outlined in this Policy.</li>
            <li>Comply with legal, accounting, or regulatory requirements (no longer than <strong>3 years</strong> after account deletion unless required by law).</li>
          </ul>
          <p>User Content may be kept in anonymized or aggregated form for analytical improvement of our AI models.</p>

          <hr />

          <h2>7. Cookies & Similar Technologies</h2>
          <p>We use cookies, pixels, and local storage to:</p>
          <ul>
            <li>Authenticate users and maintain sessions.</li>
            <li>Remember preferences.</li>
            <li>Measure engagement and performance.</li>
            <li>Conduct analytics.</li>
          </ul>
          <p>You can control cookies via your browser settings; however, disabling cookies may limit Platform functionality.</p>
          <p>
            When you draw a birth chart or open Your Astrology Today, your birth details are saved in a cookie on this device so the daily reading can open without asking again. The server uses those details to calculate a result and does not keep them. Forget the chart here, or clear cookies in your browser.
          </p>
          <ForgetChart />

          <hr />

          <h2>8. Security</h2>
          <p>We employ administrative, technical, and physical safeguards such as encryption at rest and in transit, firewall protection, and role‑based access controls. No method of transmission or storage is 100% secure; therefore we cannot guarantee absolute security.</p>

          <hr />

          <h2>9. International Data Transfers</h2>
          <p>We are based in the United States. If you access the Platform from outside the U.S., your information will be transferred to, stored, and processed in the U.S. We rely on standard contractual clauses or other approved mechanisms for cross‑border data transfers when applicable.</p>

          <hr />

          <h2>10. Your Rights & Choices</h2>
          <p>Depending on your location, you may have rights to:</p>
          <ul>
            <li><strong>Access</strong> or obtain a copy of personal information.</li>
            <li><strong>Correct</strong> inaccurate data.</li>
            <li><strong>Delete</strong> your information.</li>
            <li><strong>Restrict</strong> or <strong>object</strong> to processing.</li>
            <li><strong>Data Portability</strong>.</li>
            <li><strong>Opt‑out</strong> of marketing emails (via unsubscribe link or account settings).</li>
          </ul>
          <p>To exercise these rights, please contact us using the address at the bottom of the page. We will respond within 30 days.</p>

          <p><strong>California Rights</strong></p>
          <p>California residents have additional rights, including the right to know categories of data collected, sources, purposes, and disclosures. We do not sell or share personal information for cross‑context behavioral advertising.</p>

          <hr />

          <h2>11. Children's Privacy</h2>
          <p>The Platform is intended for users <strong>18 years or older</strong>. We do not knowingly collect personal information from anyone under 18. If you believe a minor has provided personal information, contact us to delete it.</p>

          <hr />

          <h2>12. Changes to This Policy</h2>
          <p>We may update this Policy periodically. We will notify you of material changes by posting the revised Policy on this page and updating the "Last updated" date. Continued use of the Platform after changes take effect constitutes acceptance.</p>

          <hr />

          <h2>13. Contact Us</h2>
          <p>If you have questions or concerns about this Policy or our privacy practices, please contact us through our <Link to="/support">support page</Link>.</p>

          <hr />

          <p><strong>By using the Platform, you acknowledge that you have read, understood, and agreed to this Privacy Policy.</strong></p>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Privacy; 